import { apiClient, request } from "@/shared/api/client";

export const QUESTION_CATEGORIES = [
  { value: "구매", label: "구매" },
  { value: "앱", label: "앱" },
  { value: "학습", label: "학습" },
  { value: "오류", label: "오류" },
  { value: "배송", label: "배송" },
  { value: "기타", label: "기타" },
] as const;

export type QuestionCategory = (typeof QUESTION_CATEGORIES)[number]["value"];

interface AddQuestionDto {
  title: string;
  content: string;
  category: string;
  section: string;
  name?: string;
  email?: string;
  phone_number?: string;
  is_public?: boolean;
}

interface UpdateQuestionDto {
  title: string;
  content: string;
  category: string;
}

interface QuestionResponse {
  id: string;
  category: string;
  section: string;
  title: string;
  content: string;
  comment?: string;
  is_active: boolean;
  is_public?: boolean;
  email?: string;
  phone_number?: string;
  author?: {
    id: string;
    name: string;
    email?: string;
  };
  created_at: string;
  updated_at?: string;
}

export type { QuestionResponse };

/**
 * 문의(Questions) API: 순수 HTTP 통신만 담당
 * 백엔드와의 통신을 담당합니다
 */
class QuestionsAPI {
  /**
   * 나의 1:1 문의 내역 조회
   * @returns 나의 문의 목록
   */
  async getMyQuestions(): Promise<QuestionResponse[]> {
    try {
      const response = await apiClient.get<any>(`/questions`);
      const data = response.data;

      // 응답이 배열인 경우
      if (Array.isArray(data)) {
        return data;
      }
      // 응답이 객체이고 data 속성이 있는 경우
      if (data && typeof data === "object" && Array.isArray(data.data)) {
        return data.data;
      }
      // 응답이 객체이고 questions 속성이 있는 경우
      if (data && typeof data === "object" && Array.isArray(data.questions)) {
        return data.questions;
      }

      return [];
    } catch (err) {
      throw this.handleError(err);
    }
  }

  async addQuestion(data: AddQuestionDto): Promise<QuestionResponse> {
    try {
      const response = await apiClient.post<QuestionResponse>(`/questions`, {
        ...data,
        section: "inquiries",
      });
      return response.data;
    } catch (err) {
      throw this.handleError(err);
    }
  }

  /**
   * 카테고리별 질문 조회
   * @param category 카테고리
   * @returns 카테고리별 문의 목록
   */
  async getCategoryQuestions(
    category: string = "inquiries"
  ): Promise<QuestionResponse[]> {
    try {
      const response = await apiClient.get<any>(
        `/questions/category/${category}`
      );
      const data = response.data;

      // 응답이 배열인 경우
      if (Array.isArray(data)) {
        return data;
      }
      // 응답이 객체이고 data 속성이 있는 경우
      if (data && typeof data === "object" && Array.isArray(data.data)) {
        return data.data;
      }
      // 응답이 객체이고 questions 속성이 있는 경우
      if (data && typeof data === "object" && Array.isArray(data.questions)) {
        return data.questions;
      }

      return [];
    } catch (err) {
      throw this.handleError(err);
    }
  }

  /**
   * 섹션별 질문 조회
   * @param section 섹션
   * @param category 카테고리 (선택사항)
   * @returns 섹션별 문의 목록
   */
  async getSectionQuestions(
    section: string,
    category?: string
  ): Promise<QuestionResponse[]> {
    try {
      const params = category ? `?category=${category}` : "";
      const response = await apiClient.get<any>(
        `/questions/section/${section}${params}`
      );
      const data = response.data;

      // 응답이 배열인 경우
      if (Array.isArray(data)) {
        return data;
      }
      // 응답이 객체이고 data 속성이 있는 경우
      if (data && typeof data === "object" && Array.isArray(data.data)) {
        return data.data;
      }
      // 응답이 객체이고 questions 속성이 있는 경우
      if (data && typeof data === "object" && Array.isArray(data.questions)) {
        return data.questions;
      }

      return [];
    } catch (err) {
      throw this.handleError(err);
    }
  }

  /**
   * 문의 수정
   * @param questionId 문의 ID
   * @param data 수정할 데이터
   * @returns 수정된 문의
   */
  async updateQuestion(
    questionId: string,
    data: UpdateQuestionDto
  ): Promise<QuestionResponse> {
    try {
      const response = await apiClient.put<QuestionResponse>(
        `/questions/${questionId}`,
        data
      );
      return response.data;
    } catch (err) {
      throw this.handleError(err);
    }
  }

  /**
   * 에러 처리
   */
  private handleError(err: any): Error {
    if (err.response?.data?.message) {
      return new Error(err.response.data.message);
    }
    if (err.message) {
      return new Error(err.message);
    }
    return new Error("API 호출 실패");
  }
}

export const questionsAPI = new QuestionsAPI();
